import { AppConfig } from '@config';
import { hasOwnProperty } from '@endurancewarranty/lib';
import { CustomValidationError } from '@lib/errors/CustomValidationError';
import { HttpErrorResponse } from '@lib/errors/HttpErrorResponse';
import { setUrlPathParams } from '@lib/utils';
import { normalizeUrl } from '@lib/utils/normalizeUrl';

import type { TCustomValidationException } from '@app/_components/error/CustomValidationException';
import type { TBody } from '@lib/types/THttpRequest';
import type {
  THttpHeaders,
  THttpMethod,
  THttpRequest,
  TPayloadValue,
} from '@lib/types/THttpRequest';
import type { THttpResponse, THttpResponseOptions, TReturnable } from '@lib/types/THttpResponse';

/**
 * Builder class to interact with external APIs.
 *
 * This class should __not__ be called directly when making HTTP requests.
 *
 * Use the following method instead:
 * ```tsx
 * const { http } = useHttp();
 * const req = http.get(...);
 * ```
 */
export class HttpRequest {
  private abortController: Nullable<AbortController> = null;
  private config = {} as THttpRequest;
  private fullUrl: Nullable<string> = null;
  private headers = {} as THttpHeaders;
  private tokenFetcher: () => Promise<Maybe<string>>;
  private tokenRefresher: (() => Promise<Maybe<string>>) | null = null;

  protected constructor(endpoint?: string) {
    this.config.endpoint = endpoint ?? '';
    this.headers = {};

    this.tokenFetcher = async () => null;
  }

  protected setTokenFetcher(tokenFetcher: () => Promise<Maybe<string>>): this {
    this.tokenFetcher = tokenFetcher;
    return this;
  }

  protected setTokenRefresher(tokenRefresher: () => Promise<Maybe<string>>): this {
    this.tokenRefresher = tokenRefresher;
    return this;
  }

  protected setHeaders(headers: Record<string, string>): this {
    this.headers = headers;
    return this;
  }

  /**
   * Set the base URL + endpoint to be used in request.
   * Only used for non KITT Gateway APIs.
   */
  public setFullUrl(url: string): this {
    this.fullUrl = url;
    return this;
  }

  public setId(id: string | number): this {
    this.config.endpoint = setUrlPathParams(this.config.endpoint, id);
    return this;
  }

  protected addHeaders(headers: Record<string, string>): this {
    this.headers = { ...this.headers, ...headers };
    return this;
  }

  protected setMethod(method: THttpMethod): this {
    this.config.method = method;
    return this;
  }

  protected setBody(body: TBody): this {
    this.config.body = body;
    return this;
  }

  protected setAbortController(abortController: AbortController): this {
    this.abortController = abortController;
    return this;
  }

  protected setQuery(query: Record<string, TPayloadValue>): this {
    this.config.query = query;
    return this;
  }

  protected async send<T extends TReturnable>(
    responseOptions?: Maybe<THttpResponseOptions>,
  ): Promise<THttpResponse<T>> {
    const token = await this.tokenFetcher();
    const correlationId = crypto.randomUUID();

    const url = normalizeUrl(
      this.fullUrl != null
        ? `${this.fullUrl}${this.buildQueryParams}`
        : `${AppConfig.gatewayUrl}/${this.config.endpoint}${this.buildQueryParams()}`,
    );

    this.headers['Authorization'] = `Bearer ${token}`;
    this.headers['x-correlation-id'] = correlationId;

    const body = this.buildBody();

    if (!(body instanceof FormData)) {
      this.headers['Content-Type'] = 'application/json;charset;UTF-8';
    }

    const signal = this.buildSignal(responseOptions);

    let response = await fetch(url, {
      method: this.config.method,
      headers: this.headers,
      body: body,
      signal,
    });

    // On 401 from Gateway, force a session refresh and retry once with a fresh token.
    if (response.status === 401 && this.tokenRefresher != null) {
      const freshToken = await this.tokenRefresher();

      if (freshToken != null && freshToken !== token) {
        response = await fetch(url, {
          method: this.config.method,
          headers: { ...this.headers, Authorization: `Bearer ${freshToken}` },
          body: body,
          signal: this.buildSignal(responseOptions),
        });
      }
    }

    if (process.env.NODE_ENV !== 'production') {
      console.info(`(${response.status}) ` + `${this.config.method} => ${this.config.endpoint} \n`);
    }

    if (response.redirected) {
      return null as THttpResponse<T>;
    }

    // Need to manually catch HTTP errors.
    // fetch() only throws errors if it's an network issue (e.g no internet).
    if (!response.ok) {
      const customError = await this.isCustomError(response);
      if (customError != null) {
        throw new CustomValidationError(customError);
      }

      const errorMessage = await this.buildErrorMessage(response);
      throw new HttpErrorResponse(response, {
        correlationId: correlationId,
        endpoint: this.config.endpoint,
        method: this.config.method,
        statusCode: response.status,
        statusMessage: errorMessage ?? response.statusText,
      });
    }

    // Ensure we do not error out on responses with no json e.g 204
    try {
      const res = responseOptions?.responseType === 'blob' ? response.blob() : response.json();
      return (await res) as THttpResponse<T>;
    } catch {
      return null as THttpResponse<T>;
    }
  }

  private buildQueryParams(): string {
    if (this.config.query == null) {
      return '';
    }

    const cleanQuery = Object.keys(this.config.query).reduce((prev, curr) => {
      if (this.config.query?.[curr] != null) {
        return { ...prev, [curr]: this.config.query[curr] };
      }
      return prev;
    }, {});

    return `?${new URLSearchParams(cleanQuery as Record<string, string>).toString()}`;
  }

  private buildSignal(responseOptions?: Maybe<THttpResponseOptions>): Nullable<AbortSignal> {
    if (this.abortController != null) {
      return this.abortController.signal;
    }

    const timeout = responseOptions?.timeout ?? AppConfig.defaultApiTimeout;
    return timeout != null ? AbortSignal.timeout(timeout) : null;
  }

  private buildBody(): Nullable<string | FormData> {
    if (this.config.body instanceof FormData) {
      return this.config.body;
    }
    return this.config.body != null ? JSON.stringify(this.config.body) : null;
  }

  private async buildErrorMessage(response: Response): Promise<string> {
    let errorMessage = null;

    try {
      // We use clone here so that we can get the body again later (eg: a mutation's onError)
      const body = await response.clone().json();
      if (response.status === 400) {
        const errors: string[] = [];

        Object.keys(body?.errors).forEach((key) => {
          body?.errors[key].forEach((error: string) => errors.push(error));
        });
        errorMessage = errors.join(', ');
      } else {
        errorMessage = body?.message;
      }
      // eslint-disable-next-line no-empty
    } catch {}

    return errorMessage;
  }

  private async isCustomError(response: Response): Promise<Maybe<TCustomValidationException>> {
    const body = await response.clone().json();

    if (body == null) {
      return null;
    }

    if (!hasOwnProperty(body, 'errorType')) {
      return null;
    }

    return body as TCustomValidationException;
  }
}
